import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiRequest } from '../auth/authConfig';
import { useLocalAuth } from '../auth/local-auth';
import { apiClient } from './http';

/**
 * Hook que devuelve una función para hacer llamadas autenticadas a la API.
 *
 * Prioridad del token:
 *   1. Token local (signup/login email+password) desde localStorage.
 *   2. Token de MSAL (Entra ID real o mock de dev).
 *
 * Asi, despues de hacer signup, las llamadas al backend llevan el JWT
 * del nuevo tenant sin pasar por MSAL.
 *
 * Silent refresh:
 *   Cuando el backend devuelve 401 con un token local, asumimos que el
 *   access (TTL 1h) ha expirado. Llamamos /auth/refresh con el refresh
 *   token persistido (TTL 5d, sliding window) y reintentamos la misma
 *   peticion una sola vez. Si el refresh tambien falla, propagamos el
 *   401 para que la UI redirija al login.
 */
export function useApi() {
  const { token: localToken, refresh, getRefreshToken } = useLocalAuth();
  const { instance, accounts } = useMsal();

  async function getToken(): Promise<string> {
    if (localToken) return localToken;
    if (!accounts[0]) throw new Error('No hay sesion activa (local ni MSAL)');
    try {
      const result = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0],
      });
      return result.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ ...apiRequest, account: accounts[0] });
      }
      throw e;
    }
  }

  /**
   * Refresh + retry para un único fallo 401. Solo aplica a auth local;
   * con MSAL el refresh lo gestiona la libreria.
   */
  async function refreshAndRetry<T>(
    config: AxiosRequestConfig,
    originalError: unknown,
  ): Promise<T> {
    if (!getRefreshToken()) throw originalError;
    try {
      const newToken = await refresh();
      const res = await apiClient.request<T>({
        ...config,
        headers: { ...config.headers, Authorization: `Bearer ${newToken}` },
      });
      return res.data;
    } catch (refreshErr) {
      // Si el refresh falla (refresh token expirado o invalido), limpiamos
      // la sesion local para que useApi() no se quede atascado pidiendo
      // un refresh que nunca va a funcionar.
      if (axios.isAxiosError(refreshErr) && refreshErr.response?.status === 401) {
        localStorage.removeItem('deca:auth:token');
        localStorage.removeItem('deca:auth:refresh');
        localStorage.removeItem('deca:auth:user');
      }
      throw originalError;
    }
  }

  async function request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
    const token = await getToken();
    try {
      const res = await apiClient.request<T>({
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        },
      });
      return res.data;
    } catch (err) {
      const isLocalAuth = !!getRefreshToken();
      const is401 = axios.isAxiosError(err) && err.response?.status === 401;
      const isAuthEndpoint = typeof config?.url === 'string' && config.url.startsWith('/auth/');
      if (isLocalAuth && is401 && !isAuthEndpoint) {
        return refreshAndRetry<T>(config, err);
      }
      throw err;
    }
  }

  return {
    get: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
      request<T>({ ...config, url, method: 'GET' }),
    post: <T = unknown>(url: string, data?: any, config?: AxiosRequestConfig) =>
      request<T>({ ...config, url, method: 'POST', data }),
    patch: <T = unknown>(url: string, data?: any, config?: AxiosRequestConfig) =>
      request<T>({ ...config, url, method: 'PATCH', data }),
    del: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
      request<T>({ ...config, url, method: 'DELETE' }),
  };
}

/** Helper para errores user-friendly */
export function getErrorMessage(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const ax = e as AxiosError<{ message?: string }>;
    return ax.response?.data?.message ?? ax.message;
  }
  if (e instanceof Error) return e.message;
  return 'Error desconocido';
}
